import React, { useState, useRef, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Stack,
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { HeaderButton, DropdownTextField } from "../../../components";
import { SimCardDownload, CloudUpload, CheckCircle } from "@mui/icons-material";
import { uploadUsersSample } from "../../../assets";
import { api, urls } from "../../../services";
import { useAuth, useToast } from "../../../contexts";

const useStyles = makeStyles(() => ({
  statChip: {
    fontSize: 13,
    padding: "8px 4px",
  },
  invalidRow: {
    fontSize: 12,
    color: "#EB363F",
  },
}));

export const ExcelUpload = (props) => {
  const { open, onClose, onSubmit } = props;
  const { logout } = useAuth();
  const { showToast } = useToast();
  const classes = useStyles();
  const uplodRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || subscriptions.length > 0) return;
    api
      .get(`${urls.subscriptionList}?page=1&limit=50`)
      .then((res) => {
        const plans = (res.data?.data?.subscription_plans || []).filter(
          (p) => p.subscriptions && p.subscriptions.length > 0
        );
        setSubscriptions(plans);
        setPlan(plans[0] || null);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message, "error");
        }
      });
  }, [open]);

  const subscriptionId =
    plan && plan.subscriptions && plan.subscriptions.length > 0
      ? plan.subscriptions[0].id
      : null;

  const resetState = () => {
    setResult(null);
    setUploading(false);
    if (uplodRef.current) uplodRef.current.value = "";
  };

  const handleDone = () => {
    resetState();
    onSubmit();
    onClose();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const extension = file.name.split(".").pop();
      if (extension !== "xls" && extension !== "xlsx") {
        showToast("Only .xls or .xlsx files are allowed", "error");
        uplodRef.current.value = "";
        return;
      }
      if (!subscriptionId) {
        showToast("Subscription plan not loaded yet. Please try again.", "error");
        return;
      }

      setUploading(true);

      const formData = new FormData();
      formData.append("excel_file", file);
      formData.append("subscription_id", subscriptionId);

      api
        .post(urls.excelUserUpload, formData)
        .then((res) => {
          setUploading(false);
          const counts = res.data?.data?.new_users !== undefined ? res.data.data : null;
          setResult({ message: res.data.message, counts: counts });
        })
        .catch((err) => {
          setUploading(false);
          if (err.response?.status === 401) {
            logout();
          } else {
            showToast(err.response?.data?.message, "error");
          }
        });
    }
  };

  return (
    <Dialog open={open} sx={{ p: 4 }} fullWidth>
      <DialogTitle textAlign={"center"}>Users Upload Excel</DialogTitle>
      <DialogContent>
        <Container
          maxWidth="md"
          sx={{
            justifyContent: "center",
          }}
        >
          {result ? (
            <Stack alignItems={"center"} spacing={2}>
              <CheckCircle color="success" sx={{ fontSize: 44 }} />
              <Typography variant="subtitle1" align="center">
                {result.message}
              </Typography>
              {result.counts && (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  justifyContent="center"
                  useFlexGap
                >
                  <Chip
                    className={classes.statChip}
                    label={`${result.counts.new_users} new`}
                    color="primary"
                  />
                  <Chip
                    className={classes.statChip}
                    label={`${result.counts.renewed} renewed`}
                    color="secondary"
                  />
                  <Chip
                    className={classes.statChip}
                    label={`${result.counts.already_subscribed} already subscribed`}
                    sx={{ background: "#ECECEC" }}
                  />
                  {result.counts.invalid > 0 && (
                    <Chip
                      className={classes.statChip}
                      label={`${result.counts.invalid} invalid rows skipped`}
                      color="error"
                    />
                  )}
                </Stack>
              )}
              {result.counts &&
                result.counts.invalid_rows &&
                result.counts.invalid_rows.length > 0 && (
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Skipped rows:
                    </Typography>
                    {result.counts.invalid_rows.map((r, i) => (
                      <Typography key={i} className={classes.invalidRow}>
                        Row {r.row}: {r.reason}
                      </Typography>
                    ))}
                  </Stack>
                )}
            </Stack>
          ) : (
            <Stack alignItems={"center"} spacing={2}>
              {!uploading && (
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <a href={uploadUsersSample} download="upload-users-sample.xlsx">
                    <Button
                      sx={{ textTransform: "none" }}
                      startIcon={<SimCardDownload />}
                    >
                      Download Template
                    </Button>
                  </a>

                  <Button
                    sx={{ textTransform: "none" }}
                    startIcon={<CloudUpload />}
                    onClick={() => uplodRef.current.click()}
                  >
                    Upload File
                    <input
                      type="file"
                      hidden
                      accept=".xls,.xlsx"
                      onChange={handleFileChange}
                      ref={uplodRef}
                    />
                  </Button>
                </Stack>
              )}

              {!uploading && subscriptions.length > 0 && (
                <Box sx={{ width: "100%", maxWidth: 360 }}>
                  <DropdownTextField
                    label={"Subscription Plan"}
                    options={subscriptions}
                    values={plan}
                    noClearIcon={true}
                    optionKey={"name"}
                    onChange={(e, value) => setPlan(value)}
                  />
                </Box>
              )}

              {uploading && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress />
                  <Typography>Uploading...</Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Container>
      </DialogContent>
      <DialogActions sx={{ p: 4, pt: 0 }}>
        <Stack spacing={2} direction="row">
          {result ? (
            <Fragment>
              <HeaderButton type="secondary" onClick={resetState}>
                Upload Another
              </HeaderButton>
              <HeaderButton type="primary" onClick={handleDone}>
                Done
              </HeaderButton>
            </Fragment>
          ) : (
            <HeaderButton
              type="secondary"
              onClick={() => {
                resetState();
                onClose();
              }}
              disabled={uploading}
            >
              Cancel
            </HeaderButton>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

ExcelUpload.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};
